import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiCheck, FiCopy } from 'react-icons/fi'
import { ImpersonationUser, ImpersonationUsersPage } from './types'
import * as S from './styles'

type Props = {
  users: ImpersonationUser[]
  selectedId?: string
  pagination: ImpersonationUsersPage['pagination']
  loading: boolean
  onSelect: (user: ImpersonationUser) => void
  onPageChange: (page: number) => void
}

export function ImpersonationUserTable({ users, selectedId, pagination, loading, onSelect, onPageChange }: Props) {
  const { t } = useTranslation()
  const tr = (key: string, options?: object) => t(`translation:impersonation.${key}` as any, options as any)
  const [copied, setCopied] = useState('')

  async function copy(value: string) {
    await navigator.clipboard.writeText(value)
    setCopied(value)
    window.setTimeout(() => setCopied(current => (current === value ? '' : current)), 1600)
  }

  function values(items: string[]) {
    const filtered = items.filter(Boolean)
    if (!filtered.length) return <S.Muted>—</S.Muted>
    return (
      <S.ValueList title={filtered.join(', ')}>
        {filtered.slice(0, 2).map((value, index) => (
          <S.Tag key={`${value}-${index}`}>{value}</S.Tag>
        ))}
        {filtered.length > 2 && <S.MoreTag>+{filtered.length - 2}</S.MoreTag>}
      </S.ValueList>
    )
  }

  function copyable(value: string) {
    return (
      <S.CopyValue>
        <span>{value || '—'}</span>
        {value && (
          <S.CopyButton
            type="button"
            aria-label={copied === value ? tr('copied') : tr('copy')}
            title={copied === value ? tr('copied') : tr('copy')}
            onClick={event => {
              event.stopPropagation()
              copy(value).catch(() => undefined)
            }}
          >
            {copied === value ? <FiCheck /> : <FiCopy />}
          </S.CopyButton>
        )}
      </S.CopyValue>
    )
  }

  return (
    <>
      <S.TableScroll>
        <S.Table>
          <thead>
            <tr>
              <S.HeaderCell>{tr('name')}</S.HeaderCell>
              <S.HeaderCell>{tr('email')}</S.HeaderCell>
              <S.HeaderCell>{tr('userId')}</S.HeaderCell>
              <S.HeaderCell>{tr('campaigns')}</S.HeaderCell>
              <S.HeaderCell>{tr('companies')}</S.HeaderCell>
            </tr>
          </thead>
          <tbody>
            {!loading && users.length === 0 && (
              <tr>
                <S.EmptyCell colSpan={5}>{tr('noUsers')}</S.EmptyCell>
              </tr>
            )}
            {users.map(user => (
              <S.TableRow
                key={user.id}
                $selected={selectedId === user.id}
                onClick={() => onSelect(user)}
                onKeyDown={event => event.key === 'Enter' && onSelect(user)}
                tabIndex={0}
                aria-selected={selectedId === user.id}
              >
                <S.Cell>
                  <S.UserName>{user.name || '—'}</S.UserName>
                </S.Cell>
                <S.Cell>{copyable(user.email)}</S.Cell>
                <S.Cell>{copyable(user.id)}</S.Cell>
                <S.Cell>{values(user.campaigns?.map(campaign => campaign.name) || [])}</S.Cell>
                <S.Cell>{values(user.companies?.map(company => company.name) || [])}</S.Cell>
              </S.TableRow>
            ))}
          </tbody>
        </S.Table>
        {loading && <S.LoadingState>{tr('searching')}</S.LoadingState>}
      </S.TableScroll>
      <S.Pagination>
        <S.PageInfo>{tr('page', { page: pagination.page, total: pagination.total })}</S.PageInfo>
        <div>
          <S.Button disabled={loading || pagination.page <= 1} onClick={() => onPageChange(pagination.page - 1)}>
            {tr('previous')}
          </S.Button>
          <S.Button disabled={loading || !pagination.hasNext} onClick={() => onPageChange(pagination.page + 1)}>
            {tr('next')}
          </S.Button>
        </div>
      </S.Pagination>
    </>
  )
}
